import { useApolloClient } from '@apollo/client'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getEventScopeCacheKey } from '~/common/utils/eventScopeCacheKey'
import { cacheRecentEvent, readRecentEvents } from '~/components/activity/eventCache'
import { ACTIVITY_LIMIT } from '~/components/activity/TimelineDrawer'
import {
  BreadcrumbObjectType,
  GetPaginatedCommentsQuery,
  GetPaginatedEventsQuery,
  PushActivityFeedEventDocument,
  PushActivityFeedEventSubscription,
  PushCommentDocument,
  PushCommentSubscription,
  useGetActivityFeedRowScopesQuery,
  useGetPaginatedCommentsLazyQuery,
  useGetPaginatedEventsLazyQuery,
} from '../generated/apollo-operations'
import { useDeviceType } from './useDeviceQuery'
import { DossSignalType, useDossSignals } from './useDossSignals'
import { useGlobalContext } from './useGlobalContext'
import { usePage } from './usePage'
import { useTypedTranslation } from './useTypedTranslation'

export type ActivityFeedTab = 'activity' | 'comments'

export interface ActivityFeedScopeOption {
  id: string
  label: string
  objectId: string
  objectType: BreadcrumbObjectType
  parentRowId?: string
}

type ActivityFeedContextType = {
  activeTab: ActivityFeedTab
  canToggleRowEvents: boolean
  canViewActivity: boolean
  comments: GetPaginatedCommentsQuery['paginatedComments']['comments']
  events: GetPaginatedEventsQuery['paginatedEvents']['events']
  fetchMore: () => Promise<void>
  hasMore: boolean
  includeRowEvents: boolean
  loading: boolean
  scopeOptions: ActivityFeedScopeOption[]
  selectedScope: ActivityFeedScopeOption | undefined
  setActiveTab: (tab: ActivityFeedTab) => void
  setIncludeRowEvents: (include: boolean) => void
  setSelectedScopeId: (scopeId: string) => void
}

export function ActivityFeedProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTypedTranslation()
  const { isPageWithActivityFeed, objectId, objectType } = usePage()
  const { isMobile } = useDeviceType()
  const { subscribe, unsubscribe } = useDossSignals()
  const apolloClient = useApolloClient()
  const { isAdmin, isInternal } = useGlobalContext()
  const canViewActivity = isAdmin || isInternal
  const [activeTab, setActiveTab] = useState<ActivityFeedTab>('activity')
  const [selectedScopeId, setSelectedScopeId] = useState<string | null>(null)
  const [includeRowEvents, setIncludeRowEvents] = useState(false)

  // Track if activity feed drawer is currently open.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const isTableRowPage = objectType === BreadcrumbObjectType.TABLE_ROW && objectId != null
  const { data: rowScopesData } = useGetActivityFeedRowScopesQuery({
    variables: { tableRowId: objectId ?? '' },
    skip: !isDrawerOpen || !isTableRowPage,
  })

  const scopeOptions = useMemo<ActivityFeedScopeOption[]>(() => {
    if (!isTableRowPage || !rowScopesData?.tableRow) {
      return []
    }

    const childTables = [...rowScopesData.tableRow.table.childTables].sort(
      (a, b) => (a.orderOnRecordDetails ?? 0) - (b.orderOnRecordDetails ?? 0),
    )
    return [
      {
        id: `row:${rowScopesData.tableRow.id}`,
        label: rowScopesData.tableRow.name || t('activity.scope.parent_row'),
        objectId: rowScopesData.tableRow.id,
        objectType: BreadcrumbObjectType.TABLE_ROW,
      },
      ...childTables.map((childTable) => ({
        id: `child-table:${childTable.id}`,
        label: childTable.name,
        objectId: childTable.id,
        objectType: BreadcrumbObjectType.TABLE,
        parentRowId: rowScopesData.tableRow.id,
      })),
    ]
  }, [isTableRowPage, rowScopesData?.tableRow, t])

  const fallbackScope = useMemo<ActivityFeedScopeOption | undefined>(() => {
    if (!objectId || !objectType) {
      return undefined
    }
    return {
      id: `object:${objectType}:${objectId}`,
      label: '',
      objectId,
      objectType,
    }
  }, [objectId, objectType])

  const selectedScope = useMemo<ActivityFeedScopeOption | undefined>(() => {
    if (selectedScopeId !== null) {
      const matchingScope = scopeOptions.find((scopeOption) => scopeOption.id === selectedScopeId)
      if (matchingScope) {
        return matchingScope
      }
    }

    if (scopeOptions.length > 0) {
      return scopeOptions[0]
    }

    return fallbackScope
  }, [fallbackScope, scopeOptions, selectedScopeId])

  const canToggleRowEvents =
    selectedScope?.objectType === BreadcrumbObjectType.TABLE && selectedScope.parentRowId == null
  const effectiveIncludeRowEvents = canToggleRowEvents && includeRowEvents

  const [
    getPaginatedEvents,
    { data: eventsData, loading: eventsLoading, subscribeToMore: subscribeToMoreEvents, fetchMore },
  ] = useGetPaginatedEventsLazyQuery({
    notifyOnNetworkStatusChange: true,
  })
  const [
    getPaginatedComments,
    {
      data: commentsData,
      loading: commentsLoading,
      subscribeToMore: subscribeToMoreComments,
      fetchMore: fetchMoreComments,
    },
  ] = useGetPaginatedCommentsLazyQuery({
    notifyOnNetworkStatusChange: true,
  })

  // Listen for drawer expanded/collapsed signals to manage subscription lifecycle.
  useEffect(() => {
    const activityFeedExpandedSubscription = subscribe(
      DossSignalType.ACTIVITY_FEED_EXPANDED,
      async () => {
        setIsDrawerOpen(true)
      },
    )

    const activityFeedCollapsedSubscription = subscribe(
      DossSignalType.ACTIVITY_FEED_COLLAPSED,
      async () => {
        setIsDrawerOpen(false)
        apolloClient.cache.evict({ fieldName: 'paginatedEvents' })
        apolloClient.cache.evict({ fieldName: 'paginatedComments' })
        apolloClient.cache.gc()
      },
    )

    return () => {
      unsubscribe(activityFeedExpandedSubscription)
      unsubscribe(activityFeedCollapsedSubscription)
    }
  }, [apolloClient, subscribe, unsubscribe])

  // Reset drawer state when the page object changes.
  useEffect(() => {
    setActiveTab('activity')
    setSelectedScopeId(null)
    setIncludeRowEvents(false)
  }, [objectId, objectType])

  // Load the active drawer tab from the split event/comment queries.
  useEffect(() => {
    if (isMobile || !isPageWithActivityFeed || !isDrawerOpen) {
      return
    }

    if (activeTab === 'activity') {
      if (!selectedScope || !canViewActivity) {
        return
      }
      void getPaginatedEvents({
        variables: {
          input: {
            objectId: selectedScope.objectId,
            objectType: selectedScope.objectType,
            parentRowId: selectedScope.parentRowId,
            includeRowEvents: effectiveIncludeRowEvents,
            limit: ACTIVITY_LIMIT,
          },
        },
        fetchPolicy: 'cache-and-network',
      })
      return
    }

    if (!objectId || !objectType) {
      return
    }
    void getPaginatedComments({
      variables: {
        input: {
          objectId,
          objectType,
          limit: ACTIVITY_LIMIT,
        },
      },
      fetchPolicy: 'cache-and-network',
    })
  }, [
    activeTab,
    canViewActivity,
    effectiveIncludeRowEvents,
    getPaginatedComments,
    getPaginatedEvents,
    isDrawerOpen,
    isMobile,
    isPageWithActivityFeed,
    objectId,
    objectType,
    selectedScope,
  ])

  // Subscribe to live events for the selected ClickHouse feed scope while the drawer is open.
  useEffect(() => {
    if (
      isMobile ||
      !isPageWithActivityFeed ||
      !isDrawerOpen ||
      activeTab !== 'activity' ||
      !selectedScope ||
      !canViewActivity
    ) {
      return
    }

    const unsubscribeEvent = subscribeToMoreEvents({
      document: PushActivityFeedEventDocument,
      variables: {
        input: {
          objectId: selectedScope.objectId,
          objectType: selectedScope.objectType,
          parentRowId: selectedScope.parentRowId,
          includeRowEvents: effectiveIncludeRowEvents,
        },
      },
      updateQuery: (prev, { subscriptionData }) => {
        const newData = subscriptionData.data as unknown as PushActivityFeedEventSubscription
        const pushed = newData.pushActivityFeedEvent
        // Persist to localStorage so the event survives a refresh during ClickHouse ingest lag.
        cacheRecentEvent(
          getEventScopeCacheKey({ ...selectedScope, includeRowEvents: effectiveIncludeRowEvents }),
          pushed,
        )
        // A table's own events belong to both toggle states, so cache under the other key too.
        if (canToggleRowEvents && pushed.objectType !== BreadcrumbObjectType.TABLE_ROW) {
          cacheRecentEvent(
            getEventScopeCacheKey({
              ...selectedScope,
              includeRowEvents: !effectiveIncludeRowEvents,
            }),
            pushed,
          )
        }
        const previousData = prev.paginatedEvents as
          | undefined
          | GetPaginatedEventsQuery['paginatedEvents']
        return {
          __typename: 'Query',
          paginatedEvents: {
            cursor: previousData?.cursor ?? null,
            events: [pushed],
            hasNext: !!previousData?.hasNext,
            __typename: previousData?.__typename ?? 'PaginatedEvents',
          },
        }
      },
    })

    return () => {
      unsubscribeEvent()
    }
  }, [
    activeTab,
    canToggleRowEvents,
    canViewActivity,
    effectiveIncludeRowEvents,
    isDrawerOpen,
    isMobile,
    isPageWithActivityFeed,
    selectedScope,
    subscribeToMoreEvents,
  ])

  // Subscribe to new comments for the current page object while the comments tab is open.
  useEffect(() => {
    if (
      isMobile ||
      !isPageWithActivityFeed ||
      !isDrawerOpen ||
      activeTab !== 'comments' ||
      !objectId ||
      !objectType
    ) {
      return
    }

    const unsubscribeComment = subscribeToMoreComments({
      document: PushCommentDocument,
      variables: {
        input: {
          objectId,
          objectType,
        },
      },
      updateQuery: (prev, { subscriptionData }) => {
        const newData = subscriptionData.data as unknown as PushCommentSubscription
        const previousData = prev.paginatedComments as
          | undefined
          | GetPaginatedCommentsQuery['paginatedComments']
        return {
          __typename: 'Query',
          paginatedComments: {
            cursor: previousData?.cursor ?? null,
            comments: [newData.pushComment],
            hasNext: !!previousData?.hasNext,
            __typename: previousData?.__typename ?? 'PaginatedComments',
          },
        }
      },
    })

    return () => {
      unsubscribeComment()
    }
  }, [
    activeTab,
    isDrawerOpen,
    isMobile,
    isPageWithActivityFeed,
    objectId,
    objectType,
    subscribeToMoreComments,
  ])

  const events = useMemo<GetPaginatedEventsQuery['paginatedEvents']['events']>(() => {
    const serverEvents = eventsData?.paginatedEvents.events ?? []
    if (!selectedScope) {
      return serverEvents
    }
    // Merge back cached events the server read doesn't include yet (ClickHouse ingest lag).
    const serverEventIds = new Set(serverEvents.map((paginatedEvent) => paginatedEvent.event.id))
    const cachedEvents = readRecentEvents(
      getEventScopeCacheKey({ ...selectedScope, includeRowEvents: effectiveIncludeRowEvents }),
    ).filter((paginatedEvent) => !serverEventIds.has(paginatedEvent.event.id))
    return cachedEvents.length > 0 ? [...serverEvents, ...cachedEvents] : serverEvents
  }, [effectiveIncludeRowEvents, eventsData?.paginatedEvents.events, selectedScope])
  const comments = useMemo<GetPaginatedCommentsQuery['paginatedComments']['comments']>(
    () => commentsData?.paginatedComments.comments ?? [],
    [commentsData?.paginatedComments.comments],
  )
  const hasMore =
    activeTab === 'activity'
      ? (eventsData?.paginatedEvents.hasNext ?? false)
      : (commentsData?.paginatedComments.hasNext ?? false)
  const loading = activeTab === 'activity' ? eventsLoading : commentsLoading

  const loadMore = useCallback(async () => {
    if (!hasMore) {
      return
    }

    if (activeTab === 'activity') {
      if (!selectedScope || !eventsData?.paginatedEvents.cursor) {
        return
      }
      await fetchMore({
        variables: {
          input: {
            objectId: selectedScope.objectId,
            objectType: selectedScope.objectType,
            parentRowId: selectedScope.parentRowId,
            includeRowEvents: effectiveIncludeRowEvents,
            cursor: eventsData.paginatedEvents.cursor,
            limit: ACTIVITY_LIMIT,
          },
        },
      })
      return
    }

    if (!objectId || !objectType || !commentsData?.paginatedComments.cursor) {
      return
    }
    await fetchMoreComments({
      variables: {
        input: {
          objectId,
          objectType,
          cursor: commentsData.paginatedComments.cursor,
          limit: ACTIVITY_LIMIT,
        },
      },
    })
  }, [
    activeTab,
    commentsData?.paginatedComments.cursor,
    effectiveIncludeRowEvents,
    eventsData?.paginatedEvents.cursor,
    fetchMore,
    fetchMoreComments,
    hasMore,
    objectId,
    objectType,
    selectedScope,
  ])

  const value = useMemo<ActivityFeedContextType>(
    () => ({
      activeTab,
      canToggleRowEvents,
      canViewActivity,
      comments,
      events,
      fetchMore: loadMore,
      hasMore,
      includeRowEvents,
      loading,
      scopeOptions,
      selectedScope,
      setActiveTab,
      setIncludeRowEvents,
      setSelectedScopeId,
    }),
    [
      activeTab,
      canToggleRowEvents,
      canViewActivity,
      comments,
      events,
      hasMore,
      includeRowEvents,
      loadMore,
      loading,
      scopeOptions,
      selectedScope,
    ],
  )

  return <ActivityFeedContext.Provider value={value}>{children}</ActivityFeedContext.Provider>
}

export const useActivityFeed = () => {
  const context = useContext(ActivityFeedContext)
  if (!context) {
    throw new Error('useActivityFeed must be used within a ActivityFeedProvider')
  }
  return context
}

const ActivityFeedContext = createContext<ActivityFeedContextType | undefined>(undefined)
